import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Platform,
  Alert,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { authApi } from "../../api/auth";
import { useAuthStore } from "../../state/authStore";
import AuthShell, { AuthField, authStyles } from "../../components/AuthShell";
import { colors } from "../../theme";

type Props = NativeStackScreenProps<ParentStackParamList, "Signup">;

export default function SignupScreen({ navigation }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const { setTokens } = useAuthStore();

  const handleGoogleToken = async (idToken: string) => {
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.google(idToken);
      setTokens(tokens.accessToken, tokens.refreshToken, tokens.parentId);
      // Google account is created/linked server-side — go straight in. New
      // Google users add their first child from the dashboard.
      (navigation as any).replace(
        Platform.OS === "web" ? "WebShell" : "ProfileSelect",
      );
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Google sign-in failed. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setError(null);
    if (!email || !password) {
      setError("Enter an email and a password (8+ characters).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const tokens = await authApi.signup(email, password, name);
      setTokens(tokens.accessToken, tokens.refreshToken, tokens.parentId);
      // Account created — set up the first child on the next screen.
      navigation.replace("AddChild", { fromOnboarding: true });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Couldn't create your account. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      mode="signup"
      eyebrow="Get started"
      title="Make space for curiosity"
      subtitle="Create a family account in under a minute. Free for 30 days, no card required."
      onSwitchMode={(m) => {
        if (m === "signin") navigation.navigate("Login");
      }}
      onGoogleToken={handleGoogleToken}
      footer={
        // Browse-first on every platform: a signed-out visitor can always get
        // back out to the free library instead of being stuck on an auth page.
        // Web has its own shell route; native has the guest home.
        <TouchableOpacity
          accessibilityRole="link"
          onPress={() =>
            (navigation as any).navigate(
              Platform.OS === "web" ? "WebShell" : "GuestHome",
            )
          }
        >
          <Text style={authStyles.link}>← Browse free videos, no account needed</Text>
        </TouchableOpacity>
      }
    >
      <AuthField label="Your name">
        <TextInput
          style={[
            authStyles.input,
            focused === "name" && authStyles.inputFocused,
          ]}
          placeholder="Parent's name"
          placeholderTextColor={colors.ink3}
          value={name}
          onChangeText={setName}
          onFocus={() => setFocused("name")}
          onBlur={() => setFocused(null)}
        />
      </AuthField>

      <AuthField label="Email">
        <TextInput
          style={[
            authStyles.input,
            focused === "email" && authStyles.inputFocused,
          ]}
          placeholder="you@home.com"
          placeholderTextColor={colors.ink3}
          autoCapitalize="none"
          keyboardType="email-address"
          value={email}
          onChangeText={setEmail}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused(null)}
        />
      </AuthField>

      <AuthField label="Password">
        <TextInput
          style={[
            authStyles.input,
            focused === "password" && authStyles.inputFocused,
          ]}
          placeholder="At least 8 characters"
          placeholderTextColor={colors.ink3}
          secureTextEntry
          value={password}
          onChangeText={setPassword}
          onFocus={() => setFocused("password")}
          onBlur={() => setFocused(null)}
        />
      </AuthField>

      {error ? <Text style={authStyles.error}>{error}</Text> : null}

      <TouchableOpacity
        accessibilityRole="button"
        style={[authStyles.submit, loading && authStyles.submitDisabled]}
        onPress={handleSignup}
        disabled={loading}
        activeOpacity={0.85}
      >
        <Text style={authStyles.submitText}>
          {loading ? "Creating…" : "Create family account"}
        </Text>
      </TouchableOpacity>

      <View style={authStyles.meta}>
        <Text style={authStyles.metaText}>Already have an account?</Text>
        <TouchableOpacity
          accessibilityRole="link"
          onPress={() => navigation.navigate("Login")}
        >
          <Text style={authStyles.link}>Sign in</Text>
        </TouchableOpacity>
      </View>

      <Text style={authStyles.tos}>
        By creating an account you agree to our Terms and Privacy Policy. We
        never show ads to children.
      </Text>
    </AuthShell>
  );
}
